import { z } from 'zod';
import {
  DETECTOR_KEYS,
  POST_FIELD_KEYS,
  SELECTOR_PACK_LOCALES,
  SELECTOR_PACK_PLATFORMS,
  SELECTOR_PACK_SCHEMA_VERSION,
  SELECTOR_RULE_KEYS,
  type DetectorKey,
  type PostFieldKey,
  type SelectorRuleKey,
} from './constants';
import { assertConditionDepth, detectorRuleSchema, type DetectorRule } from './detector';
import { fieldRuleSchema, type FieldRule } from './field';
import { locatorRuleSchema, type LocatorRule } from './locator';

/**
 * A record whose keys must come from a closed allowlist.
 *
 * This is the enforcement point for "giá trị lạ bị từ chối": a pack published
 * with a key the interpreter does not know is rejected outright rather than being
 * silently ignored. Silently ignoring it would be worse than failing — an admin
 * would see a successful publish and believe a broken selector had been fixed.
 *
 * Keys are all optional: a server pack usually overrides only the handful of
 * rules that Facebook just broke, layering on top of the agent's baseline.
 */
function closedKeyRecord<Key extends string, Value extends z.ZodTypeAny>(
  allowedKeys: readonly Key[],
  valueSchema: Value,
  label: string,
): z.ZodType<Partial<Record<Key, z.infer<Value>>>> {
  const allowed = new Set<string>(allowedKeys);

  const schema = z.record(z.string(), valueSchema).superRefine((value, ctx) => {
    for (const key of Object.keys(value)) {
      if (!allowed.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: `Unknown ${label} key "${key}". Allowed keys: ${allowedKeys.join(', ')}.`,
        });
      }
    }
  });

  // Safe: the refinement above guarantees every key is one of `allowedKeys`.
  return schema as unknown as z.ZodType<Partial<Record<Key, z.infer<Value>>>>;
}

/**
 * Scalar pacing knobs. These are data, not behaviour — the adapter decides *what*
 * to do, the pack only tunes timings and caps so an admin can adapt to a slow
 * network without shipping a new agent build.
 *
 * These delays exist to keep the agent from hammering the page and to let content
 * finish rendering. They are not, and must not become, a mechanism for imitating
 * human timing to evade detection.
 */
export const packTuningSchema = z
  .object({
    navigationTimeoutMs: z.number().int().min(1_000).max(120_000).default(30_000),
    resultCardStabilizeMs: z.number().int().min(0).max(15_000).default(1_500),
    scrollDelayMs: z.number().int().min(200).max(15_000).default(1_200),
    maxScrollsPerKeyword: z.number().int().min(1).max(100).default(12),
    maxResultsPerKeyword: z.number().int().min(1).max(500).default(60),
    betweenKeywordDelayMs: z.number().int().min(0).max(120_000).default(5_000),
    commentTypingDelayMs: z.number().int().min(0).max(5_000).default(40),
  })
  .strict();

export type PackTuning = z.infer<typeof packTuningSchema>;

export const selectorPackDocumentSchema = z
  .object({
    schemaVersion: z.literal(SELECTOR_PACK_SCHEMA_VERSION),
    platform: z.enum(SELECTOR_PACK_PLATFORMS),
    locale: z.enum(SELECTOR_PACK_LOCALES),
    /**
     * Lowest `adapterVersion` that can interpret this pack. An agent whose
     * adapter is older must refuse the pack and keep its baseline — otherwise it
     * would silently mis-drive the page.
     */
    minAdapterVersion: z.number().int().min(1).default(1),
    rules: closedKeyRecord(SELECTOR_RULE_KEYS, locatorRuleSchema, 'selector rule').default({}),
    detectors: closedKeyRecord(DETECTOR_KEYS, detectorRuleSchema, 'detector').default({}),
    fields: closedKeyRecord(POST_FIELD_KEYS, fieldRuleSchema, 'post field').default({}),
    tuning: packTuningSchema.default({}),
    notes: z.string().max(2_000).optional(),
  })
  .strict();

export type SelectorPackDocument = z.infer<typeof selectorPackDocumentSchema>;

export type SelectorRules = Partial<Record<SelectorRuleKey, LocatorRule>>;
export type DetectorRules = Partial<Record<DetectorKey, DetectorRule>>;
export type FieldRules = Partial<Record<PostFieldKey, FieldRule>>;

export interface SelectorPackValidationSuccess {
  ok: true;
  pack: SelectorPackDocument;
}

export interface SelectorPackValidationFailure {
  ok: false;
  errors: string[];
}

export type SelectorPackValidationResult =
  | SelectorPackValidationSuccess
  | SelectorPackValidationFailure;

/**
 * Validate an untrusted pack document.
 *
 * Called in two places, and both matter: the API validates before storing a new
 * version (so a bad pack never reaches any agent), and the agent validates again
 * on receipt (so a compromised or buggy server still cannot feed it something the
 * interpreter does not understand). Never skip the agent-side check.
 */
export function validateSelectorPack(input: unknown): SelectorPackValidationResult {
  const parsed = selectorPackDocumentSchema.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      errors: parsed.error.issues.map(
        (issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`,
      ),
    };
  }

  // Depth caps cannot be expressed in the Zod schema itself.
  const errors: string[] = [];
  for (const [key, detector] of Object.entries(parsed.data.detectors) as Array<
    [DetectorKey, DetectorRule]
  >) {
    try {
      assertConditionDepth(detector.condition, key);
    } catch (error) {
      errors.push(error instanceof Error ? error.message : String(error));
    }
  }

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, pack: parsed.data };
}

/**
 * Layer a server-published pack over the agent's built-in baseline.
 *
 * Merging is per named rule, not per candidate: if the server defines
 * `searchBox`, its candidate chain replaces the baseline chain wholesale. Trying
 * to merge candidate lists would produce a chain no human authored, where a stale
 * baseline entry could shadow the fix that was just published.
 */
export function mergeSelectorPacks(
  baseline: SelectorPackDocument,
  override: SelectorPackDocument | undefined,
): SelectorPackDocument {
  if (!override) return baseline;

  return {
    ...baseline,
    ...override,
    rules: { ...baseline.rules, ...override.rules },
    detectors: { ...baseline.detectors, ...override.detectors },
    fields: { ...baseline.fields, ...override.fields },
    tuning: { ...baseline.tuning, ...override.tuning },
  };
}

/** Rule keys present in a pack — used by the selector-health dashboard. */
export function listDefinedRuleKeys(pack: SelectorPackDocument): SelectorRuleKey[] {
  return Object.keys(pack.rules) as SelectorRuleKey[];
}

/**
 * Rule keys the adapter needs but the merged pack does not define. Surfacing this
 * at publish time is what stops an admin from shipping a pack that only breaks
 * once a search cycle reaches the missing step.
 */
export function findMissingRuleKeys(
  pack: SelectorPackDocument,
  requiredKeys: readonly SelectorRuleKey[],
): SelectorRuleKey[] {
  return requiredKeys.filter((key) => pack.rules[key] === undefined);
}
